import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import authUtils from '../utils/auth';
import { CheckCircle2, Loader2, Copy, KeyRound, Mail, Rocket, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ActivationSuccess = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking | activated | pending | error
  const [creds, setCreds] = useState(null);
  const [copied, setCopied] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [loggedIn, setLoggedIn] = useState(false);

  const getOrderId = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('order_id') || localStorage.getItem('activation_order_id');
  };

  const poll = useCallback(async () => {
    const orderId = getOrderId();
    if (!orderId) { setStatus('error'); return; }
    try {
      const { data } = await axios.get(`${API}/activation/status/${orderId}`);
      if (data?.activated) {
        // Instant login with the session issued by the backend
        if (data.access_token && data.user) {
          authUtils.setToken(data.access_token);
          authUtils.setUser(data.user);
          setLoggedIn(true);
        } else if (data.generatedPassword) {
          try {
            const res = await axios.post(`${API}/auth/login`, { email: data.email, password: data.generatedPassword });
            if (res.data?.access_token) { authUtils.setToken(res.data.access_token); authUtils.setUser(res.data.user); setLoggedIn(true); }
          } catch (_e) { /* user can still login manually */ }
        } else {
          try { setLoggedIn(!!JSON.parse(localStorage.getItem('user') || 'null')); } catch (_e) { /* ignore */ }
        }
        setCreds({ email: data.email, password: data.generatedPassword || null, clientId: data.clientId, name: data.name });
        setStatus('activated');
        try { localStorage.removeItem('activation_order_id'); } catch (_e) {}
      } else {
        setStatus('pending');
        setAttempts((a) => a + 1);
      }
    } catch (e) {
      setStatus('pending');
      setAttempts((a) => a + 1);
    }
  }, []);

  useEffect(() => { poll(); }, [poll]);

  // Instant access: auto-open the dashboard a few seconds after activation
  const [countdown, setCountdown] = useState(5);
  useEffect(() => {
    if (status !== 'activated' || !loggedIn) return undefined;
    if (countdown <= 0) { navigate('/dashboard', { replace: true }); return undefined; }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [status, countdown, navigate, loggedIn]);
  useEffect(() => {
    if (status === 'pending' && attempts < 20) {
      const t = setTimeout(poll, 3000);
      return () => clearTimeout(t);
    }
  }, [status, attempts, poll]);

  const copy = (text, key) => {
    navigator.clipboard?.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 1500);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
        {status === 'activated' && creds ? (
          <>
            <div className="bg-gradient-to-br from-green-500 to-emerald-500 p-8 text-center text-white">
              <CheckCircle2 className="w-16 h-16 mx-auto mb-3" />
              <h1 className="text-2xl font-extrabold">Activated Successfully!</h1>
              <p className="text-green-50 mt-1">Welcome to DropSquadz, {creds.name}. Your account is ready.</p>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-600 text-center">Save these login details (also works on our mobile app):</p>
              <div className="space-y-3">
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                  <div className="text-xs text-slate-500 flex items-center gap-1 mb-1"><Mail className="w-3 h-3" /> Email / Login ID</div>
                  <div className="flex items-center justify-between"><span className="font-semibold text-slate-800 text-sm sm:text-base min-w-0 truncate mr-2" title={creds.email}>{creds.email}</span>
                    <button onClick={() => copy(creds.email, 'email')} className="text-blue-600 text-xs flex items-center gap-1"><Copy className="w-3 h-3" />{copied === 'email' ? 'Copied' : 'Copy'}</button></div>
                </div>
                {creds.password ? (
                <div className="bg-blue-50 rounded-xl p-3 border border-blue-200">
                  <div className="text-xs text-blue-600 flex items-center gap-1 mb-1"><KeyRound className="w-3 h-3" /> Your Password</div>
                  <div className="flex items-center justify-between"><span className="font-bold text-blue-800 text-lg">{creds.password}</span>
                    <button onClick={() => copy(creds.password, 'pwd')} className="text-blue-600 text-xs flex items-center gap-1"><Copy className="w-3 h-3" />{copied === 'pwd' ? 'Copied' : 'Copy'}</button></div>
                </div>
                ) : (
                  <div className="bg-blue-50 rounded-xl p-3 border border-blue-200 text-sm text-blue-800">Use your existing password to log in next time.</div>
                )}
                <div className="text-center text-xs text-slate-400">Client ID: #{creds.clientId}</div>
              </div>
              <Button onClick={() => navigate(loggedIn ? '/dashboard' : '/', { replace: true })} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-base font-semibold rounded-xl" data-testid="activation-goto-dashboard">
                <Rocket className="w-5 h-5 mr-2" /> {loggedIn ? `Go to Dashboard${countdown > 0 ? ` (${countdown})` : ''}` : 'Log in to Dashboard'}
              </Button>
              <p className="text-xs text-center text-slate-500">Next: complete your onboarding inside the dashboard so we can start work.</p>
            </div>
          </>
        ) : status === 'error' ? (
          <div className="p-8 text-center">
            <XCircle className="w-14 h-14 text-red-500 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-slate-800">Order not found</h1>
            <p className="text-slate-500 mt-1">We couldn't find your activation order. If money was deducted, contact support on WhatsApp.</p>
            <Button onClick={() => navigate('/')} className="mt-5 bg-blue-600 hover:bg-blue-700 text-white">Back to Home</Button>
          </div>
        ) : (
          <div className="p-10 text-center">
            <Loader2 className="w-14 h-14 text-blue-600 mx-auto mb-4 animate-spin" />
            <h1 className="text-xl font-bold text-slate-800">Confirming your payment…</h1>
            <p className="text-slate-500 mt-2">This can take a few seconds. Please don't close this page.</p>
            {attempts >= 20 && (
              <div className="mt-4 text-sm text-slate-500">
                Taking longer than usual. If the amount was deducted, your account will activate shortly — please contact support on WhatsApp with your email.
                <div><Button onClick={() => navigate('/')} variant="outline" className="mt-3">Back to Home</Button></div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivationSuccess;
